import { type Collection, List as ImmutableList } from 'immutable';
import _memoize from 'lodash/memoize';

import { getLogger } from '../../utils/core';
import { chartOfAccountsSelectors } from '../chart-of-accounts';
import type { FilterObject } from '../filters/filtersTypes';
import { transactionsTypes, transactionsUtils } from '../transactions';
import type { TransactionProps } from '../transactions/transactionsTypes';

const log = getLogger('data/transactionList/selectors');

export function transactionsSortFunction(a: TransactionProps, b: TransactionProps) {
  // order by date first
  if (a.postedOn > b.postedOn) {
    return 1;
  }
  if (b.postedOn > a.postedOn) {
    return -1;
  }

  // we sort balance adjustments to the top
  const aBalanceVal = a.coa?.type === 'BALANCE_ADJUSTMENT' ? 1 : 0;
  const bBalanceVal = b.coa?.type === 'BALANCE_ADJUSTMENT' ? 1 : 0;
  const catCompare = aBalanceVal - bBalanceVal;
  if (catCompare) {
    return catCompare;
  }
  // compare ID's if nothing else works
  const va = Number(a.id) || Number(b.id) + 1;
  const vb = Number(b.id) || Number(a.id) + 1;
  return va - vb;
}

type SortByOpt =
  | 'payee'
  | 'check'
  | 'amount'
  | 'category'
  | 'reviewed'
  | 'status'
  | 'state'
  | 'date'
  | 'postedOn'
  | 'action'
  | 'security'
  | 'description'
  | 'quantity'
  | 'price'
  | 'commission';

export function sortTransactions(
  txns: Collection<string, TransactionProps>,
  sortBy: SortByOpt = 'date',
  sortOrder: 'descending' | 'ascending' = 'descending',
) {
  log.log('In selector performing SORT', sortBy, sortOrder);
  const sortFactor = sortOrder === 'ascending' ? 1 : -1;

  const sortedTxns = ImmutableList(txns.toList()).sort((a, b) => {
    let cmpValue = 0;
    switch (sortBy) {
      case 'payee':
        cmpValue = 1;

        if (a.payee === b.payee) {
          cmpValue = transactionsSortFunction(a, b);
          // sometimes payee can be null
        } else if ((a.payee || '').toLowerCase() < (b.payee || '').toLowerCase()) {
          cmpValue = -1;
        }
        break;
      case 'check': {
        cmpValue = 1;

        const aChecknum = (a.check && a.check.number) || '';
        const bChecknum = (b.check && b.check.number) || '';

        if (aChecknum === bChecknum) {
          cmpValue = transactionsSortFunction(a, b);
        } else if (Number.isNaN(Number(aChecknum)) || Number.isNaN(Number(bChecknum))) {
          cmpValue = aChecknum.toLowerCase() < bChecknum.toLowerCase() ? -1 : 1;
        } else {
          cmpValue = Number(bChecknum) - Number(aChecknum);
          // cmpValue = -1;
        }
        break;
      }
      case 'amount':
        cmpValue = 1;

        if (Number(a.amount) === Number(b.amount)) {
          cmpValue = transactionsSortFunction(a, b);
        } else if (Number(a.amount) < Number(b.amount)) {
          cmpValue = -1;
        }
        break;
      case 'category': {
        const strA = a.coa ? chartOfAccountsSelectors.getCoaStringSelector(undefined, a.coa, true) : '';
        const strB = b.coa ? chartOfAccountsSelectors.getCoaStringSelector(undefined, b.coa, true) : '';
        cmpValue = 1;

        if (strA === strB) {
          cmpValue = transactionsSortFunction(a, b);
        } else if (strA.toLowerCase() < strB.toLowerCase()) {
          cmpValue = -1;
        }
        break;
      }

      case 'reviewed':
      case 'status':
      case 'state':
        cmpValue =
          transactionsTypes.statusOrder.indexOf(a.state ?? 'PENDING') -
          transactionsTypes.statusOrder.indexOf(b.state ?? 'PENDING');
        if (cmpValue === 0) {
          // for cash-flow uses, secondary-sort needs to be on date
          cmpValue = transactionsSortFunction(a, b);
        }
        break;

      case 'date':
      case 'postedOn':
        cmpValue = transactionsSortFunction(a, b);
        break;

      case 'action':
        {
          const valueA = a.investmentTxnType ? transactionsUtils.InvestmentTxnActionsByType[a.investmentTxnType] : '';
          const valueB = b.investmentTxnType ? transactionsUtils.InvestmentTxnActionsByType[b.investmentTxnType] : '';
          cmpValue = valueA?.localeCompare(valueB);
        }
        break;
      case 'security':
        {
          const valueA = `${a.symbol} ${a.securityName}`;
          const valueB = `${b.symbol} ${b.securityName}`;
          cmpValue = valueA?.localeCompare(valueB);
        }
        break;
      case 'description':
        {
          const valueA = transactionsUtils.investmentTransactionDescription(a);
          const valueB = transactionsUtils.investmentTransactionDescription(b);
          cmpValue = valueA?.localeCompare(valueB);
        }
        break;
      case 'quantity':
        {
          const valueA = Number.isNaN(Number(a.units)) ? 0 : Math.abs(a.units ?? 0);
          const valueB = Number.isNaN(Number(b.units)) ? 0 : Math.abs(b.units ?? 0);
          cmpValue = valueA - valueB;
        }
        break;
      case 'price':
        cmpValue = (transactionsUtils.calcPricePerUnit(a) ?? 0) - (transactionsUtils.calcPricePerUnit(b) ?? 0);
        break;
      case 'commission':
        cmpValue = (a.commission ?? 0) - (b.commission ?? 0);
        break;
      default:
        verify(false, `unexpected sortBy = '${sortBy}' key`);
        break;
    }

    // if all else equal, sort by ID to guarantee repeatable sorting
    if (cmpValue === 0) {
      cmpValue = Number(a.id) - Number(b.id);
    }

    return cmpValue * sortFactor;
  });

  return sortedTxns;
}

export const createFilterFromFilterObject = _memoize((filterObject: FilterObject | null | undefined) => {
  if (filterObject) {
    return (txns: Array<TransactionProps> | Collection<string | number, TransactionProps>) =>
      transactionsUtils.filterTransactionsByFilterObject(txns, filterObject);
  }
  return null;
});
